import {
  DeleteConfirmationModal,
  DropdownMenu,
  Horizontal3DotsIcon,
} from "components";
import React, { useMemo, useState } from "react";
import { fileActionOptions, starredFileActionOptions } from "../constants";
import {
  useDeleteKbRecords,
  useMoveKbRecords,
  useToggleKbRecordsStarredStatus,
} from "controller";
import toast from "react-hot-toast";
import { KbFileListDrawer } from "./KbFileListDrawer";
import { useLocation } from "react-router-dom";
import { Box } from "@mui/material";

export const KbThreeDots = ({
  fileDetails,
  isSelected,
  isDefaultFolder,
  type,
  onActionClick,
}: {
  fileDetails: any;
  isSelected: boolean;
  isDefaultFolder: boolean;
  type: "file" | "folder";
  onActionClick?: (type: string) => void;
}) => {
  const location = useLocation();
  const optionType = type === "file" ? "File" : "Folder";
  const pathSegment = location.pathname.split("/").pop() || "overview";
  const { mutateAsync: deleteKbRecords } = useDeleteKbRecords();
  const { mutateAsync: toggleKbRecordsStarredStatus } =
    useToggleKbRecordsStarredStatus();
  const { mutateAsync: moveKbRecords } = useMoveKbRecords();

  const [isOpen, setIsOpen] = useState(false);
  const [isDeletingConfirmationOpen, setIsDeletingConfirmationOpen] =
    useState(false);

  const handleMoveRecords = async (item: string[]) => {
    try {
      await moveKbRecords({
        parent_id: item[0],
        record_ids: [fileDetails?.mapping_id],
      });
      toast.success(`${optionType} moved successfully`);
    } catch (error) {
      toast.error(`Failed to move ${type}`);
    }
  };

  const handleDelete = async () => {
    try {
      await deleteKbRecords([fileDetails?.mapping_id]);
      setIsDeletingConfirmationOpen(false);
      toast.success(`${optionType} deleted successfully`);
    } catch (error) {
      toast.error(`Failed to delete ${type}`);
    }
  };

  const handleOptionClick = async ({ value }: { value: string }) => {
    onActionClick?.(value);
    switch (value) {
      case "star":
        try {
          await toggleKbRecordsStarredStatus({
            record_ids: [fileDetails?.mapping_id],
            is_starred: !fileDetails?.is_starred,
          });
          toast.success(`${optionType} starred successfully`);
        } catch (error) {
          toast.error(`Failed to star ${type}`);
        }
        break;
      case "unstar":
        try {
          await toggleKbRecordsStarredStatus({
            record_ids: [fileDetails?.mapping_id],
            is_starred: false,
          });
          toast.success(`${optionType} unstarred successfully`);
        } catch (error) {
          toast.error(`Failed to unstar ${type}`);
        }
        break;
      case "move":
        setIsOpen(true);
        break;
      case "rename":
        break;
      case "delete":
        setIsDeletingConfirmationOpen(true);
        break;
    }
  };

  const getActionOptions = useMemo(() => {
    let fileOptions = fileActionOptions;
    switch (pathSegment) {
      case "starred":
        fileOptions = starredFileActionOptions;
        break;
      default:
        break;
    }
    if (isDefaultFolder) {
      fileOptions = fileOptions.filter(
        (option) =>
          option.value !== "move" &&
          option.value !== "rename" &&
          option.value !== "delete",
      );
    }
    if (type === "file") {
      fileOptions = fileOptions.filter((option) => option.value !== "rename");
    }
    return fileOptions;
  }, [pathSegment, isDefaultFolder, type]);

  return (
    <>
      <DropdownMenu
        options={getActionOptions}
        onOptionClick={({ value }) => {
          handleOptionClick({ value: value || "" });
        }}
        anchorOriginHorizontal="right"
        sx={{
          ml: "auto",
        }}
      >
        <Horizontal3DotsIcon
          color="action"
          sx={{
            visibility: isSelected ? "hidden" : "visible",
          }}
        />
      </DropdownMenu>
      {isOpen && (
        <KbFileListDrawer
          title="Move to"
          primaryButtonText="Move"
          onPrimaryButtonClick={(item) => {
            handleMoveRecords(item);
            setIsOpen(false);
          }}
          onSecondaryButtonClick={() => {
            setIsOpen(false);
          }}
          onClose={() => {
            setIsOpen(false);
          }}
          open={isOpen}
          selectedValues={[]}
        />
      )}
      {isDeletingConfirmationOpen && (
        <Box onClick={(e) => e.stopPropagation()}>
          <DeleteConfirmationModal
            open={isDeletingConfirmationOpen}
            onClose={() => {
              setIsDeletingConfirmationOpen(false);
            }}
            onSecondaryButtonClick={() => {
              setIsDeletingConfirmationOpen(false);
            }}
            onPrimaryButtonClick={handleDelete}
            title={`Delete ${type}`}
            tagline={`Are you sure you want to delete this ${type}?`}
          />
        </Box>
      )}
    </>
  );
};
